/* ═══════════════════════════════════════════════════════════════
   DIY PRINTZ — Contract Cart Lock
   Detects contract-order line items and replaces qty controls
   with EDIT ORDER / DELETE ORDER buttons.

   Contract orders use "cents-as-quantity" pricing: the variant is
   $0.01 and the quote total is encoded as `quantity = total * 100`.
   NEVER reset the quantity — doing so destroys the customer's total.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const CONTRACT_HANDLE = 'contract-dtf-print-press';
  const ORDER_FORM_URL = '/pages/contract-dtf-order';

  function fmt(n) { return '$' + Number(n).toFixed(2); }

  async function fetchCart() {
    const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
    return r.json();
  }

  function isContractItem(item) {
    const props = item.properties || {};
    return item.handle === CONTRACT_HANDLE
      || props['_contract_token']
      || props._contract_token
      || props['_draft_token']
      || props._draft_token
      || props['_workorder_id']
      || props._workorder_id;
  }

  function findContractItems(cart) {
    return cart.items
      .map((item, index) => ({ ...item, _index: index }))
      .filter(isContractItem);
  }

  async function decorateContractLines() {
    const cart = await fetchCart();
    const contractItems = findContractItems(cart);

    // Toggle a body-level flag so CSS can hide theme-specific qty steppers /
    // Remove links that live OUTSIDE the .is-contract element. In the DIY
    // Printz theme, .eb-qty and .eb-line__remove are siblings of the price
    // cell that gets .is-contract, so a descendant selector on .is-contract
    // won't reach them. Body-level flag sidesteps that.
    document.body.classList.toggle('contract-cart-active', contractItems.length > 0);

    if (contractItems.length === 0) return;

    contractItems.forEach(item => {
      const nodes = findLineDom(item);
      if (!nodes.length) return;

      nodes.forEach(node => {
        if (node.dataset.contractDecorated === '1') return;
        node.dataset.contractDecorated = '1';
        node.classList.add('is-contract', 'cart-item');
        renderContractSummary(node, item);
        renderContractControls(node, item);
      });
    });
  }

  function findLineDom(item) {
    const key = item.key || item.id;
    const nodes = [];

    document.querySelectorAll(
      [
        `[data-cart-item-key="${key}"]`,
        `[data-line-item-key="${key}"]`,
        `[data-key="${key}"]`,
        `[data-variant-id="${item.variant_id}"]`,
        `tr[id^="CartItem-"]`,
        `.cart__row`,
        `.cart-item`,
        `.cst-cart-item`
      ].join(',')
    ).forEach(el => {
      const inputKey = el.querySelector('input[name="updates[]"], input[name*="quantity"]');
      const dataKey = el.dataset.cartItemKey || el.dataset.lineItemKey || el.dataset.key || '';
      if (
        dataKey === key ||
        (inputKey && (inputKey.dataset.key === key || inputKey.dataset.cartItemKey === key)) ||
        el.querySelector(`a[href*="${item.handle}"]`) ||
        el.textContent.includes(item.title)
      ) {
        nodes.push(el);
      }
    });

    // Dedupe by identity, then drop any node that CONTAINS or IS-CONTAINED-BY
    // another matched node — loose selectors match a row and its inner wrapper.
    // Keep the OUTERMOST match.
    const uniq = Array.from(new Set(nodes));
    return uniq.filter(n => !uniq.some(o => o !== n && o.contains(n)));
  }

  function renderContractSummary(node, item) {
    const props = item.properties || {};
    if (node.querySelector('.contract-line-summary')) return;

    const dl = document.createElement('dl');
    dl.className = 'contract-line-summary';

    const rows = [
      ['Company',  props['Company']],
      ['Contact',  props['Contact']],
      ['Job',      props['Order Name'] || props['Job Name']],
      ['PO',       props['PO Number']],
      ['Designs',  props['Designs']],
      ['Sizes',    props['Print Sizes']],
      ['Garments', props['Total Garments']],
      ['Quote',    props['Quote Total']]
    ].filter(([, v]) => v);

    rows.forEach(([k, v]) => {
      const dt = document.createElement('dt'); dt.textContent = k;
      const dd = document.createElement('dd'); dd.textContent = v;
      dl.append(dt, dd);
    });

    const titleEl = node.querySelector(
      '.cart-item__name, .cart-item__title, .cst-cart-item__title, h2, h3, .product-title'
    ) || node;
    titleEl.after ? titleEl.after(dl) : node.appendChild(dl);
  }

  function renderContractControls(node, item) {
    if (node.querySelector('.contract-line-ctrls')) return;

    const bar = document.createElement('div');
    bar.className = 'contract-line-ctrls';

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'contract-edit-btn';
    editBtn.textContent = 'EDIT ORDER';
    editBtn.addEventListener('click', () => editContractOrder(item));

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'contract-delete-btn';
    delBtn.textContent = 'DELETE ORDER';
    delBtn.addEventListener('click', () => deleteContractOrder(item));

    bar.append(editBtn, delBtn);

    const qtyEl = node.querySelector('.cart-item__quantity, .quantity, .cst-qty, [data-quantity-selector]');
    if (qtyEl && qtyEl.parentNode) {
      qtyEl.parentNode.insertBefore(bar, qtyEl);
    } else {
      node.appendChild(bar);
    }
  }

  function editContractOrder(item) {
    const props = item.properties || {};
    const token = props['_contract_token'] || props._contract_token
                || props['_draft_token']    || props._draft_token
                || props['_workorder_id']   || props._workorder_id
                || '';
    location.href = ORDER_FORM_URL + (token ? `?edit=${encodeURIComponent(token)}` : '');
  }

  async function deleteContractOrder(item) {
    if (!confirm('DELETE CONTRACT ORDER?\n\nThis removes ALL items in this contract order from your cart. Your saved draft can be recovered by clicking EDIT ORDER before deleting.')) return;

    // Clearing the cart is simpler and more reliable than per-line updates
    // for contract orders (which should always be the only item in cart).
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        note: '',
        attributes: {
          'contract_order': '',
          'contract_job': '',
          'contract_po': '',
          'contract_company': '',
          'contract_contact': '',
          'contract_dropoff': '',
          'contract_garments': '',
          'contract_token': '',
          'contract_workorder_url': '',
          'quote_total': ''
        }
      })
    });

    location.reload();
  }

  // ─── Enforce single contract order per cart ──────────────────
  // If somehow there are multiple contract lines (shouldn't happen — the
  // form clears the cart before add) keep the MOST RECENTLY ADDED one.
  // Shopify appends new items to the end of cart.items, so the last item
  // is the newest. We keep .pop() (last) and remove everything else.
  async function enforceSingleContract() {
    const cart = await fetchCart();
    const contractItems = findContractItems(cart);
    if (contractItems.length <= 1) return;

    const items = contractItems.slice();
    const keep = items.pop(); // newest = last
    const remove = items;

    const updates = {};
    remove.forEach(item => { updates[item.key] = 0; });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  }

  // ─── Init ────────────────────────────────────────────────────
  // IMPORTANT: we do NOT intercept quantity change events any more.
  // Contract orders use cents-as-quantity — the quantity IS the price.
  // Resetting qty to 1 would silently destroy the customer's total.
  // The quantity stepper is hidden by contract-cart-lock.css instead,
  // so customers can't manipulate it.
  function init() {
    if (!location.pathname.includes('/cart')) return;
    decorateContractLines();
    enforceSingleContract();
    // Re-decorate after theme AJAX cart updates
    const observer = new MutationObserver(() => decorateContractLines());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
